
import { api } from './base';
import type { MassIncidentInfo } from '@/entities/ticket';

export type TemplateScope = 'personal' | 'shared' | 'group';

export interface ResponseTemplate {
  id: string;
  ownerId: string;
  name: string;
  body: string;
  scope: TemplateScope;
  groupId: string | null;
  folder: string | null;
  tags: string[];
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  authorName?: string | null;
}

export interface TemplateListParams {
  q?: string;
  folder?: string;
  tag?: string;
  favoritesOnly?: boolean;
  scope?: TemplateScope;
  groupId?: string;
  limit?: number;
  offset?: number;
}

export interface TemplateCounts {
  personal: number;
  shared: number;
  favorites: number;
  groups: Record<string, number>;
}

export interface TemplateListPayload {
  data: ResponseTemplate[];
  total: number;
  favoriteIds: string[];
  folders: string[];
  groups?: { id: string; name: string }[];
  counts?: TemplateCounts;
}

export type TemplateApplyMode = 'public_reply' | 'internal_note' | 'email';

export const responseTemplatesApi = {
  list: (params?: TemplateListParams) =>
    api.get<ResponseTemplate[]>(
      '/api/v1/response-templates',
      params as Record<string, string | number | boolean | undefined>,
    ) as Promise<TemplateListPayload>,

  create: (dto: {
    name: string;
    body: string;
    scope?: TemplateScope;
    groupId?: string | null;
    folder?: string | null;
    tags?: string[];
  }) => api.post<ResponseTemplate>('/api/v1/response-templates', dto),

  update: (
    id: string,
    dto: {
      name?: string;
      body?: string;
      scope?: TemplateScope;
      groupId?: string | null;
      folder?: string | null;
      tags?: string[];
    },
  ) => api.patch<ResponseTemplate>(`/api/v1/response-templates/${id}`, dto),

  toggleFavorite: (id: string) =>
    api.put<{ favorite: boolean }>(`/api/v1/response-templates/${id}/favorite`, {}),
  remove: (id: string) => api.delete<unknown>(`/api/v1/response-templates/${id}`),
  apply: (ticketId: string, templateId: string, mode: TemplateApplyMode) =>
    api.post<{ mode: TemplateApplyMode; renderedBody: string; messageId?: string }>(
      `/api/v1/tickets/${ticketId}/templates/${templateId}/apply`,
      { mode },
    ),
};

export interface MassIncidentSummary extends MassIncidentInfo {
  auto: boolean;
  createdAt: string;
  ticketsTotal: number;
  ticketsOpen: number;
  categoryIds: string[];
  subcategoryIds: string[];
}

export interface MassIncidentAddResult {
  incident: MassIncidentSummary;
  added: string[];
  skipped: { ticketId: string; reason: string }[];
}

export const massIncidentsApi = {
  mark: (ticketId: string, dto?: { incidentId?: string; title?: string }) =>
    api.post<MassIncidentInfo>(`/api/v1/tickets/${ticketId}/mass-incident`, dto ?? {}),
  unmark: (ticketId: string) => api.delete<unknown>(`/api/v1/tickets/${ticketId}/mass-incident`),
  list: (status: 'active' | 'resolved' = 'active') =>
    api.get<MassIncidentSummary[]>('/api/v1/mass-incidents', { status }),
  get: (id: string) => api.get<MassIncidentSummary>(`/api/v1/mass-incidents/${id}`),
  update: (id: string, dto: { title?: string; status?: 'active' | 'resolved' }) =>
    api.patch<MassIncidentSummary>(`/api/v1/mass-incidents/${id}`, dto),
  addTickets: (ticketIds: string[], target: { incidentId: string } | { title?: string }) =>
    'incidentId' in target
      ? api.post<MassIncidentAddResult>(`/api/v1/mass-incidents/${target.incidentId}/tickets`, { ticketIds })
      : api.post<MassIncidentAddResult>('/api/v1/mass-incidents', {
          ticketIds,
          ...(target.title?.trim() ? { title: target.title.trim() } : {}),
        }),
};

export interface MassIncidentRule {
  id: string;
  categoryId: string | null;
  subcategoryId: string | null;
  threshold: number;
  windowMinutes: number;
  enabled: boolean;
}

export const massIncidentRulesApi = {
  list: () => api.get<MassIncidentRule[]>('/api/v1/admin/mass-incident-rules'),
  create: (dto: Omit<MassIncidentRule, 'id' | 'enabled'> & { enabled?: boolean }) =>
    api.post<MassIncidentRule>('/api/v1/admin/mass-incident-rules', dto),
  update: (id: string, dto: Partial<Pick<MassIncidentRule, 'threshold' | 'windowMinutes' | 'enabled'>>) =>
    api.patch<MassIncidentRule>(`/api/v1/admin/mass-incident-rules/${id}`, dto),
  remove: (id: string) => api.delete<unknown>(`/api/v1/admin/mass-incident-rules/${id}`),
};

export function collectClientInfo(): Record<string, unknown> {
  return {
    platform: navigator.platform,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
